import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import { FormDescription, FormLabel } from "@babackapp/ui/components/form";
import { ScrollArea } from "@babackapp/ui/components/scroll-area";
import { ChevronDownIcon, TrashIcon } from "@radix-ui/react-icons";
import { ComponentProps, Fragment, useMemo } from "react";
import FormInputContainer from "./form-input-container";

type FormMultiOptionsSelectorOption = {
  label: string;
  key: string;
};

export type FormMultiOptionsSelectorProps = {
  label: string;
  description?: string;
  values: string[];
  options: FormMultiOptionsSelectorOption[];
  onChange: (value: string[]) => void;
  hideKey?: boolean;
  hideSelected?: boolean;
};

type Checked = ComponentProps<typeof DropdownMenuCheckboxItem>["checked"];

export default function FormMultiOptionsSelector({
  values,
  onChange,
  options,
  label,
  hideSelected,
  description,
  hideKey,
}: FormMultiOptionsSelectorProps) {
  const t = useTranslation();

  const onOptionChange = (
    option: FormMultiOptionsSelectorOption,
    checked: Checked
  ) => {
    if (checked) {
      onChange([...values, option.key]);
    } else {
      onChange(values.filter((value) => value !== option.key));
    }
  };

  const selectedOptions = useMemo(() => {
    return options.filter((option) => values.includes(option.key));
  }, [values, options]);

  const checked = useMemo(() => {
    return selectedOptions.reduce(
      (acc, option) => {
        acc[option.key] = true;
        return acc;
      },
      {} as Record<string, boolean>
    );
  }, [selectedOptions]);

  return (
    <FormInputContainer label={label} description={description}>
      <div className="flex flex-row gap-2 items-start flex-wrap">
        {!hideSelected &&
          selectedOptions.map((option) => (
            <SelectedItem
              key={option.key}
              option={option}
              onClick={() => onOptionChange(option, false)}
            />
          ))}

        <DropdownMenu modal={true}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2">
              {hideSelected
                ? // @ts-expect-error plural forms cannot be typed for now
                  t("misc.selected", { count: selectedOptions.length })
                : label}
              <ChevronDownIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-72">
            <DropdownMenuLabel>{label}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <ScrollArea className="pr-4 max-h-96 overflow-scroll">
              {options.map((option) => (
                <Fragment key={option.key}>
                  <DropdownMenuCheckboxItem
                    checked={checked[option.key]}
                    onCheckedChange={(v) => onOptionChange(option, v)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    <div className="space-y-2 leading-normal pr-2">
                      <FormLabel>{option.label}</FormLabel>
                      {!hideKey && (
                        <FormDescription>{option.key}</FormDescription>
                      )}
                    </div>
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                </Fragment>
              ))}
            </ScrollArea>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </FormInputContainer>
  );
}

type SelectedItemProps = {
  option: FormMultiOptionsSelectorOption;
  onClick?: () => void;
};

function SelectedItem({ option, onClick }: SelectedItemProps) {
  return (
    <Button
      variant="outline"
      className="gap-2 flex items-center hover:bg-destructive/10 group transition-all"
      onClick={onClick}
    >
      {option.label}
      <TrashIcon className="size-4 group-hover:text-destructive transition-all group-hover:scale-125 group-hover:rotate-12" />
    </Button>
  );
}
